/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from 'react';
import classNames from 'classnames';

import classes from '@nerdwallet/base-styles/classes';
import Down from '@nerdwallet/react-icon/gizmo/Navigatedown';
import Image from '@nerdwallet/react-image';
import Type from '@nerdwallet/react-typography';
import Up from '@nerdwallet/react-icon/gizmo/Navigateup';
import * as MediaQueries from '@nerdwallet/react-media-queries';
import LoadingN from '@nerdwallet/react-loading-n';
import { DEFAULT_STICKY_FOOTER_HEIGHT } from '@nerdwallet/global-nav-vars';

import ContentPageHeader from '../content-page-header/content-page-header';
import ArticleStructuredContent from '../article-structured-content/article-structured-content';
import AuthorBox from '../author-box/author-box';
import ConnectedOutdatedPageNotification from '../outdated-page-notification/outdated-page-notification';
import MonetizingLink from '../monetizing-link/monetizing-link';
import LegacyNextSteps from '../legacy-next-steps/legacy-next-steps';
import SectionNavigation from '../section-navigation/section-navigation';

import { graphqlArticlePropTypes } from '../../../lib/propTypes';
import { cdnifyImagesInString } from '../../../lib/render-utils';

import {
  getArticleGroupConfigBySlug,
  getArticleSectionNavigationItemsHierarchicalBySlug,
  getArticleSectionNavigationTitle,
  filterMediaSizes,
} from '../../../lib/article-utils';

const DIRECTION = {
  UP: 'UP',
  DOWN: 'DOWN',
};

class ResourceTemplate extends React.Component {
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- directive added automatically by Shepherd migration
  constructor(props) {
    super(props);
    this.onWindowScroll = this.onWindowScroll.bind(this);
    this.onExpanderRef = this.onExpanderRef.bind(this);
    this.onBottomNavRef = this.onBottomNavRef.bind(this);
    this.onBottomNavMarkerRef = this.onBottomNavMarkerRef.bind(this);
    this.expanderTriggerClicked = this.expanderTriggerClicked.bind(this);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- directive added automatically by Shepherd migration
  componentDidMount() {
    window.addEventListener('scroll', this.onWindowScroll);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- directive added automatically by Shepherd migration
  componentWillUnmount() {
    window.removeEventListener('scroll', this.onWindowScroll);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- directive added automatically by Shepherd migration
  onExpanderRef(ref) {
    this.expanderRef = ref;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- directive added automatically by Shepherd migration
  onBottomNavRef(ref) {
    this.bottomNavRef = ref;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- directive added automatically by Shepherd migration
  onBottomNavMarkerRef(ref) {
    this.bottomNavMarkerRef = ref;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- directive added automatically by Shepherd migration
  onWindowScroll() {
    const { scrollY } = window;
    if (scrollY < this.currentScrollY) {
      // scrolling up
      if (this.currentScrollDirection !== DIRECTION.UP) {
        // start scrolling up
        this.currentScrollDirection = DIRECTION.UP;
        this.onScrollUpStart();
      } else if (scrollY < this.scrollDownBreakpointEnd) {
        this.onScrollUpEnd();
      }
    } else if (scrollY > this.currentScrollY) {
      if (this.currentScrollDirection !== DIRECTION.DOWN) {
        // start scrolling down
        this.currentScrollDirection = DIRECTION.DOWN;
        this.onScrollDownStart();
      } else if (scrollY > this.scrollDownBreakpointStuck) {
        // scroll up past the breakpoint
        this.onScrollDownStuck();
      } else if (scrollY > this.scrollDownBreakpointEnd) {
        this.onScrollDownEnd();
      }
    }
    this.currentScrollY = scrollY;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- directive added automatically by Shepherd migration
  onScrollUpStart() {
    const expander = this.expanderRef;
    const nav = this.bottomNavRef;
    const navStyle = window.getComputedStyle(nav);

    if (navStyle.display === 'none') return;
    if (this.navExpanded) return;

    // is the expander already in the transitional position?
    if (navStyle.position === 'relative') return;
    nav.classList.add(classes(['position-relative']));
    nav.classList.remove(classes(['position-sticky']));

    const offset =
      this.bottomNavMarkerRef.offsetTop +
      this.bottomNavMarkerRef.offsetParent.offsetTop -
      window.scrollY -
      window.innerHeight +
      expander.offsetHeight;

    Object.assign(nav.style, {
      top: `-${Math.max(offset, 0)}px`,
      bottom: null,
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- directive added automatically by Shepherd migration
  onScrollUpEnd() {
    if (!this.atBottom) return;
    this.atBottom = false;
    this.expanderRef.classList.remove('at-bottom');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- directive added automatically by Shepherd migration
  onScrollDownEnd() {
    if (this.atBottom) return;
    this.atBottom = true;
    this.expanderRef.classList.add('at-bottom');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- directive added automatically by Shepherd migration
  onScrollDownStuck() {
    if (this.navExpanded) return;
    // Set header sticky
    this.scrollDownBreakpointStuck = Infinity;
    this.currentScrollDirection = DIRECTION.DOWN;

    this.bottomNavRef.classList.add(classes(['position-sticky']));
    this.bottomNavRef.classList.remove(classes(['position-relative']));

    const offset =
      this.bottomNavRef.offsetHeight - this.expanderRef.offsetHeight;

    let bottom = `calc(-${offset}px + var(--global-nav-sticky-footer-height, ${DEFAULT_STICKY_FOOTER_HEIGHT}px))`;

    // check if IE, to set non-css-var style
    if (window?.navigator?.userAgent?.includes('MSIE ')) {
      bottom = `calc(-${offset}px + ${DEFAULT_STICKY_FOOTER_HEIGHT}px)`;
    }

    Object.assign(this.bottomNavRef.style, {
      top: null,
      bottom,
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- directive added automatically by Shepherd migration
  onScrollDownStart() {
    if (this.navExpanded) return;
    // Place nav right below viewport to scroll in gracefully from the bottom
    const expander = this.expanderRef;
    const bottomNavStyle = window.getComputedStyle(this.bottomNavRef);

    if (bottomNavStyle.display === 'none') return;

    const expanderHeight = expander.offsetHeight;
    const navContainerOffsetTop = this.bottomNavMarkerRef.offsetTop;

    // get current position offset of the header.
    // If the user scrolls back up before fully scrolling down
    // and the header is still in view, we don't want it to jump
    const curBottomOffset =
      bottomNavStyle.position === 'relative'
        ? parseFloat(bottomNavStyle.top) || 0
        : 0;

    // get the position of where the header should be, just above the viewport
    const newBottomOffset =
      -1 *
      (navContainerOffsetTop -
        window.scrollY -
        window.innerHeight +
        this.bottomNavRef.offsetParent.offsetTop);
    const finalBottomOffset = Math.min(curBottomOffset, newBottomOffset);

    // set scrollY position of when we want to change from relative to sticky
    this.scrollDownBreakpointStuck =
      window.scrollY + expanderHeight - (newBottomOffset - finalBottomOffset);

    this.scrollDownBreakpointEnd =
      this.bottomNavMarkerRef.offsetTop +
      this.bottomNavMarkerRef.offsetParent.offsetTop +
      this.expanderRef.offsetHeight -
      window.innerHeight;

    if (window.scrollY > this.scrollDownBreakpointStuck) {
      // This means the scroll position jumped up higher than the height of the header
      this.onScrollDownStuck();
      return;
    }

    this.bottomNavRef.classList.add(classes(['position-relative']));
    this.bottomNavRef.classList.remove(classes(['position-sticky']));
    Object.assign(this.bottomNavRef.style, {
      top: `${finalBottomOffset}px`,
      bottom: null,
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- directive added automatically by Shepherd migration
  expanderTriggerClicked(navExpanded = !this.navExpanded) {
    const navClassName = 'bottom-navigation--expanded';

    if (this.atBottom) return;

    this.navExpanded = navExpanded;

    if (navExpanded) {
      let bottom = `var(--global-nav-sticky-footer-height, ${DEFAULT_STICKY_FOOTER_HEIGHT}px)`;

      // check if IE, to set non-css-var style
      if (window?.navigator?.userAgent?.includes('MSIE ')) {
        bottom = `${DEFAULT_STICKY_FOOTER_HEIGHT}px`;
      }

      Object.assign(this.bottomNavRef.style, {
        bottom,
        top: null,
      });
      this.bottomNavRef.classList.add(classes(['position-sticky']));
      this.bottomNavRef.classList.remove(classes(['position-relative']));
      this.bottomNavRef.classList.add(navClassName);
    } else {
      // behave like stuck
      this.onScrollDownStuck();
      this.bottomNavRef.classList.remove(navClassName);
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- directive added automatically by Shepherd migration
  render() {
    const { props } = this;
    const { graphqlArticle } = props;
    const navItems = getArticleSectionNavigationItemsHierarchicalBySlug(
      graphqlArticle.slug
    );
    const monetizingLinkData = getArticleGroupConfigBySlug(graphqlArticle.slug)
      ?.monetizingLink;
    const heading = getArticleSectionNavigationTitle(graphqlArticle.slug);
    const inlineFeaturedSourceUrl = cdnifyImagesInString(
      filterMediaSizes(graphqlArticle?.featuredImage, [
        'primary-xl',
        'primary-m',
        'primary-s',
        'full',
      ])?.mediaDetails?.sizes?.[0]?.sourceUrl ?? ''
    );

    const inlineFeaturedSmall = filterMediaSizes(
      graphqlArticle?.featuredImage,
      ['primary-xs', 'primary-xxs', 'full']
    )?.mediaDetails?.sizes?.[0];

    const renderMain = () => (
      <div>
        <div className={classes('margin-bottom-4')}>
          <ContentPageHeader article={graphqlArticle} />
        </div>
        {!graphqlArticle.hideFeaturedImage && inlineFeaturedSourceUrl && (
          <Image
            sources={[
              {
                src: `${inlineFeaturedSourceUrl}`,
                media: `${MediaQueries.wide}`,
              },
              {
                src: `${cdnifyImagesInString(inlineFeaturedSmall?.sourceUrl)}`,
                media: `${MediaQueries['not-wide']}`,
              },
            ]}
            src={inlineFeaturedSourceUrl}
            naturalWidth={inlineFeaturedSmall?.width}
            naturalHeight={inlineFeaturedSmall?.height}
            alt={graphqlArticle.title}
            className={classNames([
              'resource__featured-image',
              classes(['margin-bottom-3']),
            ])}
          />
        )}
        <ConnectedOutdatedPageNotification />
        <div className="resource__structured-content">
          <ArticleStructuredContent>
            {graphqlArticle.content}
          </ArticleStructuredContent>
        </div>
        <div ref={this.onBottomNavMarkerRef} />
        <div
          className={classNames('bottom-navigation', [
            classes(['color-neutral-darker', 'display-none--wide']),
          ])}
          ref={this.onBottomNavRef}
        >
          <div
            className="expander-trigger"
            onClick={() => this.expanderTriggerClicked()}
            ref={this.onExpanderRef}
          >
            <div className={classes(['flex-1'])}>
              <Type
                size={2}
                className={classNames([classes(['color-neutral-darker'])])}
              >
                More in
              </Type>
              <Type
                component="h5"
                size={2}
                bold
                className={classNames([classes(['color-neutral-darkest'])])}
              >
                {heading}
              </Type>
            </div>
            <div className="bottom-navigation--icon-container">
              <Up className="bottom-navigation--up" />
              <Down className="bottom-navigation--down" />
            </div>
          </div>
          <SectionNavigation
            navItems={navItems}
            currentSlug={graphqlArticle.slug}
            className={classNames(['outset', classes(['text-small'])])}
          />
          <div
            className={classNames(classes(['width-100', 'display-none--wide']))}
          >
            <MonetizingLink monetizingLinkData={monetizingLinkData} />
          </div>
        </div>
        <LegacyNextSteps
          items={graphqlArticle.whatsNext}
          article={graphqlArticle}
        />
        <AuthorBox article={graphqlArticle} />
      </div>
    );

    return (
      <article
        className={classNames([
          'resource',
          classes(['position-relative', 'text-default']),
        ])}
      >
        <div
          className={classNames([
            'resource__main',
            'container',
            classes([
              'flex-wrap',
              'justify-content-space-between',
              'clearfix',
              'display-flex',
              'flex-direction-column-reverse',
              'flex-direction-row--wide',
            ]),
          ])}
        >
          <div
            role="complementary"
            className={classNames([
              'resource__secondary',
              classes([
                'margin-bottom-5',
                'padding-bottom-5',
                'position-sticky--wide',
                'background-white',
                'height-100--wide',
                'display-block--wide',
                'display-none',
              ]),
            ])}
          >
            <SectionNavigation
              navItems={navItems}
              currentSlug={props.slug}
              className={classNames([
                'outset',
                classes([
                  'margin-bottom-2',
                  'padding-vertical-2',
                  'border-neutral-lighter',
                  'border-bottom-1',
                  'border-top-1',
                  'text-small',
                ]),
              ])}
            />
            <MonetizingLink monetizingLinkData={monetizingLinkData} />
          </div>
          <div role="main" className={classNames(['resource__primary'])}>
            {/* Hack to deal with center-align bug: isolate loading spinner and article content.
            For more context, see https://nerdwallet.atlassian.net/browse/WPRM-209 */}
            <div>
              {props.loading && (
                <div
                  className={classes([
                    'display-flex',
                    'justify-content-center',
                    'margin-vertical-5',
                  ])}
                >
                  <LoadingN type="n" />
                </div>
              )}
            </div>
            <div>{!props.loading && renderMain()}</div>
          </div>
        </div>
      </article>
    );
  }
}

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
ResourceTemplate.propTypes = graphqlArticlePropTypes;

export default ResourceTemplate;
